<template>
  <div
    v-if="isOpen && popupList.length > 0"
    class="main_notice_modal"
  >
    <!-- 딤 배경 클릭 시 닫기 ('닫기' 버튼과 동일, 쿠키 미적용) -->
    <div
      class="modal_layer"
    />
    <e-slider
      v-model="currentIndex"
      :items="popupList"
      :widths="cardWidths"
      :left-peek="40"
      :right-peek="40"
      class="modal_swiper"
    >
      <template v-slot="{ item: popup }">
        <div class="notice_slide">
          <div class="slide_tit">
            <!-- 게시판(공지사항)의 중요 라벨과 동일한 전역 스타일 사용 -->
            <span
              v-if="popup.ntcCatCd === 'Y'"
              class="label important"
            >{{ $t('msg.CMBA100.533') }}</span><!-- 중요 -->
            <span>{{ popup.messageCont }}</span>
          </div>
          <!-- 내용+링크버튼 고정 영역 : 버튼 유무와 무관하게 카드 총높이 동일 -->
          <div class="slide_cont_wrap">
            <div
              :class="['slide_cont', { img_type: popup.templUseYn === 'N' }]"
              :style="popup.templUseYn === 'Y'
                ? { 'background-color': $ekmtcCommon.setPopupBgColor(popup.templBtypeCd) }
                : null"
            >
              <!-- 이미지 일때 : 폭에 맞추고 세로로 길면 슬라이드 내부 스크롤 -->
              <img
                v-if="popup.templUseYn === 'N'"
                :src="popup.imgSrc"
              >
              <!-- 텍스트(템플릿) 일때 -->
              <div
                v-if="popup.templUseYn === 'Y'"
                class="tmpl_cont"
                v-html="$ekmtcCommon.popupTextToHtml(popup.messageDtl)"
              />
            </div>
            <div
              v-if="hasButton(popup)"
              class="btn_detail_wrap"
            >
              <a
                v-if="popup.btn1Nm && popup.btn1Nm.trim()"
                :href="popup.btn1Url"
                :title="popup.btn1Nm"
                target="_blank"
                class="btn_detail"
              >{{ popup.btn1Nm }}</a>
              <a
                v-if="popup.btn2Nm && popup.btn2Nm.trim()"
                :href="popup.btn2Url"
                :title="popup.btn2Nm"
                target="_blank"
                class="btn_detail"
              >{{ popup.btn2Nm }}</a>
            </div>
          </div>
          <!-- 닫기 바 : 카드에 포함시켜 슬라이드마다 자기 높이를 갖게 함 (admin 미리보기와 동일 보장) -->
          <div class="btn_close_wrap">
            <button
              type="button"
              class="day_close"
              @click="closeToday()"
            >{{ $t('common.NOTICE_POP.001') }}</button><!-- 오늘 하루 이 창을 열지 않기 -->
            <button
              type="button"
              class="popup_close"
              @click="isOpen = false"
            >{{ $t('msg.MAIN100.228') }}</button><!-- 닫기 -->
          </div>
        </div>
      </template>
    </e-slider>
  </div>
</template>

<script>
import { rootComputed } from '@/store/helpers'
import notices from '@/api/services/notices'
import { toServiceDomain } from '@/api/serviceDomain'
import ESlider from '@/components/common/ESlider'

// widthSize 미설정 공지의 기본 폭 (텍스트(템플릿) 공지도 이 값으로 고정, 내부 좌우 패딩 16px씩 포함)
const DEFAULT_CARD_WIDTH = 550

export default {
  name: 'MainNoticePopup',
  components: {
    ESlider
  },
  data () {
    return {
      popupList: [],
      isOpen: true,
      currentIndex: 0
    }
  },
  computed: {
    ...rootComputed,
    requireLogin () {
      return this.$store.getters['auth/requireLogin']
    },
    // 슬라이드 폭 : 이미지 공지는 등록된 widthSize(최소 500 보장), 텍스트(템플릿) 공지는 500 고정
    // 작은 사이즈(예: 100)로 등록된 이미지도 모달 자체는 줄어들지 않고, 이미지만 가운데 배치
    cardWidths () {
      return this.popupList.map(p => (p.templUseYn === 'Y' ? DEFAULT_CARD_WIDTH : Math.max(Number(p.widthSize) || DEFAULT_CARD_WIDTH, DEFAULT_CARD_WIDTH)))
    }
  },
  watch: {
    // 로그인 레이어(ID/PW 입력 ~ 프로필 선택)가 열리면 닫음
    requireLogin (v) {
      if (v) {
        this.isOpen = false
      }
    }
  },
  created () {
    // 로그인 레이어가 이미 열린 상태(세션 만료 등)로 메인에 진입한 경우 모달이 그 위를 덮지 않도록 닫힌 상태로 시작
    if (this.requireLogin) {
      this.isOpen = false
    }

    this.init()
  },
  methods: {
    async init () {
      const params = {
        serviceCtrCd: localStorage.getItem('service_ctrcd')
      }

      try {
        const res = await notices.getPopupList(params)

        // '오늘 하루 열지 않기'(기존 NoticePop 쿠키 방식) 처리된 공지는 제외
        // API가 글번호 오름차순(과거→최신)으로 내려주므로 뒤집어서 최신 공지부터 노출
        const targetList = (res.data || [])
          .filter(p => !this.hasHideTodayCookie(p.messageNum))
          .reverse()

        const detailList = await Promise.all(
          targetList.map(p =>
            notices.getPopupDetailInfo(p.messageNum)
              .then(detail => detail.data)
              .catch(() => null)
          )
        )

        const list = detailList
          .filter(detail => detail !== null)
          .map(detail => {
            if (detail.templUseYn === 'N') {
              detail.imgSrc = `${toServiceDomain(process.env.VUE_APP_COMMON_URL)}/commons/file-download/commons?messageNum=${detail.messageNum}&type=POP_MNG`
            }
            return detail
          })

        // 중요 공지(ntcCatCd='Y')를 맨 앞으로 (그룹 안에서는 최신순 유지, 구값 A/E/H는 일반 취급)
        this.popupList = [
          ...list.filter(detail => detail.ntcCatCd === 'Y'),
          ...list.filter(detail => detail.ntcCatCd !== 'Y')
        ]
      } catch (err) {
        console.log(err)
      }
    },
    hasButton (popup) {
      return (popup.btn1Nm && popup.btn1Nm.trim()) || (popup.btn2Nm && popup.btn2Nm.trim())
    },
    hasHideTodayCookie (messageNum) {
      return document.cookie.indexOf('notice' + messageNum + '=') > -1
    },
    // 오늘 하루 열지 않기 : 기존 방식(notice{messageNum} 쿠키 +1일) 그대로, 노출 중인 공지 전체에 적용
    closeToday () {
      const today = new Date()
      today.setDate(today.getDate() + 1)

      this.popupList.forEach(p => {
        const cookieKey = 'notice' + p.messageNum
        document.cookie = cookieKey + '=' + escape(cookieKey) + '; path=/; expires=' + today.toUTCString() + ';'
      })

      this.isOpen = false
    }
  }
}
</script>

<style scoped>
/* CN 접속 시 뜨는 국가별 전용 사이트 안내 모달(.popup_dim, z-index:1001)이 항상 위에 오도록 그보다 낮게 유지 */
.main_notice_modal {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal_layer {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
}
.modal_swiper {
  max-width: calc(100vw - 140px);
}
/* 닫기 바까지 카드에 포함되어 슬라이드마다 자기 높이를 가짐 → 모서리 라운드도 카드 하나로 처리 */
.notice_slide {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background-color: #ffffff;
  overflow: hidden;
}
</style>

<!-- 카드 내부(제목/내용/버튼/닫기바) 스타일 : admin 미리보기와 공용 -->
<style scoped src="./noticeCard.css"></style>
